import { Truck } from "lucide-react";

import { useProductPolicies, useProductPrices, useProductSuppliers } from "@/api/queries";
import type { PriceType, Product, ValueSource } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { formatQuantity, formatRelative, formatUnit } from "@/lib/format";

/**
 * Le produit vu du commerce (TICKET PM-02).
 *
 * Fournisseurs, seuils, historique de prix. Trois requêtes séparées parce que
 * ce sont trois endpoints séparés : personne n'a besoin de l'historique de prix
 * pour lire une fiche.
 *
 * Les relations entre produits ont leur propre carte depuis V1.5-07 : avec sept
 * types, des directions, des certitudes et un formulaire, les enfouir ici
 * rendrait les deux illisibles.
 *
 * Chaque bloc dit clairement quand il est vide plutôt que d'afficher une
 * grille de tirets. Un catalogue neuf n'a rien de tout ça, et c'est normal.
 */

const PRICE_TYPES: Record<PriceType, string> = {
  PURCHASE_ACTUAL: "Payé",
  SUPPLIER_QUOTE: "Devis",
  REFERENCE_INITIAL: "Référence",
  MARKET: "Marché",
  REPLACEMENT: "Remplacement",
  USED_MARKET: "Occasion",
  INSURED_VALUE: "Assuré",
};

const SOURCES: Record<ValueSource, string> = {
  HUMAN: "saisi",
  BARCODE: "code-barres",
  OCR: "texte lu",
  LOCAL_VISION: "modèle local",
  CLOUD_VISION: "modèle cloud",
  MANUFACTURER: "fabricant",
  CATALOGUE: "catalogue",
  SUPPLIER: "fournisseur",
  WEB: "web",
  INFERRED: "déduit",
  MEASURED: "mesuré",
  IMPORT: "import",
  SYSTEM: "système",
};

export function ProductCommercialCard({ product }: { product: Product }) {
  const { data: suppliers } = useProductSuppliers(product.id);
  const { data: policies } = useProductPolicies(product.id);
  const { data: prices } = useProductPrices(product.id);

  const globalPolicy = policies?.find((policy) => policy.location_id === null);
  const localPolicies = policies?.filter((policy) => policy.location_id !== null) ?? [];

  return (
    <Card>
      <CardHeader
        title="Commercial"
        subtitle="Chez qui on l'achète, quand le recommander, ce qu'il vaut."
      />
      <CardBody className="flex flex-col gap-4">
        <Block icon={<Truck className="size-3.5" aria-hidden />} title="Fournisseurs">
          {suppliers && suppliers.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {suppliers.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] text-ink">
                      {link.supplier_name ?? "Fournisseur"}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-ink-subtle">
                      {link.supplier_sku}
                      {link.lead_time_days !== null && ` · ${link.lead_time_days} j`}
                      {link.minimum_order_quantity !== null &&
                        ` · min ${formatQuantity(link.minimum_order_quantity)}`}
                    </span>
                  </span>
                  {link.preferred && <StatusBadge tone="info">préféré</StatusBadge>}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Aucun fournisseur déclaré.</Empty>
          )}
        </Block>

        <Block title="Seuils de réapprovisionnement">
          {globalPolicy || localPolicies.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {globalPolicy && (
                <PolicyRow
                  label="Partout"
                  min={globalPolicy.min_quantity}
                  reorder={globalPolicy.reorder_point}
                  quantity={globalPolicy.reorder_quantity}
                  unit={globalPolicy.stock_unit}
                />
              )}
              {localPolicies.map((policy) => (
                <PolicyRow
                  key={policy.id}
                  label="Emplacement précis"
                  min={policy.min_quantity}
                  reorder={policy.reorder_point}
                  quantity={policy.reorder_quantity}
                  unit={policy.stock_unit}
                />
              ))}
            </div>
          ) : (
            <Empty>Aucun seuil. Rien ne signalera un stock bas.</Empty>
          )}
        </Block>

        <Block title="Historique de prix">
          {prices && prices.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {prices.slice(0, 6).map((price) => (
                <li key={price.id} className="flex items-baseline justify-between gap-2 text-[13px]">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="tnum font-medium text-ink">
                      {formatQuantity(price.amount)} {price.currency}
                    </span>
                    <span className="truncate text-[11px] text-ink-subtle">
                      {PRICE_TYPES[price.price_type]} · {SOURCES[price.source]}
                      {price.supplier_name && ` · ${price.supplier_name}`}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] text-ink-subtle">
                    {formatRelative(price.effective_at)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Aucun prix enregistré.</Empty>
          )}
        </Block>

      </CardBody>
    </Card>
  );
}

/**
 * Le sens compte : « remplacé par » et « en remplace un » sont deux
 * informations différentes, et une équivalence n'a pas de sens du tout.
 */
function Block({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-border pt-3 first:border-0 first:pt-0">
      <p className="mb-2 flex items-center gap-1.5 text-[12px] text-ink-muted">
        {icon}
        {title}
      </p>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-[12px] text-ink-subtle">{children}</p>;
}

function PolicyRow({
  label,
  min,
  reorder,
  quantity,
  unit,
}: {
  label: string;
  min: string | null;
  reorder: string | null;
  quantity: string | null;
  unit: string | null;
}) {
  const suffix = unit ? ` ${formatUnit(unit as never)}` : "";
  return (
    <div className="flex items-baseline justify-between gap-2 text-[13px]">
      <span className="text-ink-muted">{label}</span>
      <span className="tnum text-ink">
        {min !== null && `min ${formatQuantity(min)}${suffix}`}
        {reorder !== null && ` · seuil ${formatQuantity(reorder)}${suffix}`}
        {quantity !== null && ` · commander ${formatQuantity(quantity)}${suffix}`}
      </span>
    </div>
  );
}
